/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useUnits } from '@web-stories-wp/units';

/**
 * Internal dependencies
 */
import normalizeRotationDegrees from '../utils/normalizeRotationDegrees';
import resolveAttachmentFrames from './resolveAttachmentFrames';

function useRotate({
  onGroupEventStart,
  setTransformStyle,
  onGroupEventEnd,
  targetList,
  frames,
  shouldThrottleRotation,
}) {
  const units = useUnits(({ actions }) => ({
    editorToDataX: actions.editorToDataX,
    editorToDataY: actions.editorToDataY,
    dataToEditorX: actions.dataToEditorX,
    dataToEditorY: actions.dataToEditorY,
  }));
  const onRotateGroupStart = ({ events }) => {
    onGroupEventStart({ events, isRotate: true });
  };
  const onRotateGroup = ({ events }) => {
    events.forEach(({ beforeRotate, drag }, i) => {
      const sFrame = frames[i];
      sFrame.rotate = normalizeRotationDegrees(beforeRotate);
      sFrame.translate = drag.beforeTranslate;
    });
    resolveAttachmentFrames(targetList, frames, units);
    events.forEach(({ target }, i) => {
      setTransformStyle(targetList[i].element.id, target, frames[i]);
    });
  };
  const onRotateGroupEnd = ({ targets }) => {
    onGroupEventEnd({ targets, isRotate: true });
  };
  return {
    onRotateGroup,
    onRotateGroupEnd,
    onRotateGroupStart,
    // ⇧ key rotates the element 15 degrees at a time
    throttleRotate: shouldThrottleRotation ? 15 : 0,
  };
}

export default useRotate;
